import { type ReactNode, useEffect, useState } from "react";
import { Box, Button, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useUpdateCurrentUserPassword, useUpdateCurrentUserProfile } from "src/api/client-api/useUserNew";
import { useSnackbar } from "src/utils/useSnackbar";

const sectionStyles = {
    backgroundColor: "white",
    border: "1px solid #E0DEDD",
};

// ─── Password strength ────────────────────────────────────────────────────

const computePasswordStrength = (pw: string): { score: number; label: string; color: string } => {
    if (!pw) return { score: 0, label: "", color: "" };
    let s = 0;
    if (pw.length >= 8) s++;
    if (/[A-Z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    const labels = ["", "Faible", "Moyen", "Bon", "Fort"];
    return { score: s, label: labels[s] ?? "", color: "" };
};

// ─── InfoRow ──────────────────────────────────────────────────────────────

type InfoRowProps = {
    children?: ReactNode;
    label: string;
    value?: string;
    inset?: boolean;
    noBorder?: boolean;
};

const InfoRow = ({ children, label, value, inset = false, noBorder }: InfoRowProps) => {
    const theme = useTheme();

    return (
        <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            sx={{
                minHeight: { xs: "auto", md: "56px" },
                px: inset ? 0 : { xs: theme.spacing(2.5), md: theme.spacing(4) },
                py: { xs: theme.spacing(1.5), md: 0 },
                gap: { xs: "4px", md: theme.spacing(2) },
                borderBottom: noBorder ? "none" : `1px solid ${theme.custom.grey[500]}`,
            }}
        >
            <Typography variant="body1" sx={{ fontWeight: 700, minWidth: { md: "180px" }, flexShrink: 0 }}>
                {label}
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {children ?? (
                    <Typography variant="body1" sx={{ fontWeight: 500, opacity: 0.72 }} component="div">
                        {value}
                    </Typography>
                )}
            </Box>
        </Stack>
    );
};

// ─── Strength Bar ─────────────────────────────────────────────────────────

const StrengthBar = ({ password }: { password: string }) => {
    const theme = useTheme();
    const { score, label } = computePasswordStrength(password);

    if (!password) return null;

    const colors = [
        theme.custom.grey[500],
        theme.palette.error.main,
        theme.palette.primary.main,
        theme.palette.warning.main,
        theme.palette.success.main,
    ];
    const activeColor = colors[score] ?? theme.custom.grey[500];

    return (
        <Stack gap="4px" sx={{ mt: 0.5 }}>
            <Stack direction="row" gap="4px">
                {[1, 2, 3, 4].map((i) => (
                    <Box
                        key={i}
                        sx={{
                            flex: 1,
                            height: "3px",
                            borderRadius: "2px",
                            bgcolor: i <= score ? activeColor : theme.custom.grey[500],
                            transition: "background-color 0.2s",
                        }}
                    />
                ))}
            </Stack>
            <Typography variant="caption" sx={{ color: activeColor, fontWeight: 500 }}>
                {label}
            </Typography>
        </Stack>
    );
};

// ─── Personal Info Card ───────────────────────────────────────────────────

const PersonalInfoCard = ({ email, firstName, lastName }: { email: string; firstName: string; lastName: string }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const updateProfileMutation = useUpdateCurrentUserProfile();
    const [editing, setEditing] = useState(false);
    const [localFirstName, setLocalFirstName] = useState(firstName);
    const [localLastName, setLocalLastName] = useState(lastName);

    useEffect(() => {
        if (!editing) {
            setLocalFirstName(firstName);
            setLocalLastName(lastName);
        }
    }, [editing, firstName, lastName]);

    const handleSave = async () => {
        try {
            await updateProfileMutation.mutateAsync({
                firstname: localFirstName.trim(),
                lastname: localLastName.trim(),
            });
            setSnackbarProps({
                message: t("profileSettings.personalInfo.update_success", "Profile updated."),
                type: "success",
            });
            setEditing(false);
        } catch {
            setSnackbarProps({
                message: t("profileSettings.personalInfo.update_error", "Failed to update profile."),
                type: "error",
            });
        }
    };

    const handleCancel = () => {
        setLocalFirstName(firstName);
        setLocalLastName(lastName);
        setEditing(false);
    };

    return (
        <Box sx={{ ...sectionStyles, borderRadius: theme.spacing(2), overflow: "hidden" }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    borderBottom: `1px solid ${theme.custom.grey[500]}`,
                    px: theme.spacing(4),
                    py: theme.spacing(2.5),
                }}
            >
                <Typography variant="h3" sx={{ fontSize: "20px", fontWeight: 700 }}>
                    {t("profileSettings.personalInfo.title", "Personal information")}
                </Typography>
                {!editing && (
                    <Button variant="primary" size="small" onClick={() => setEditing(true)}>
                        {t("profileSettings.personalInfo.edit", "Edit")}
                    </Button>
                )}
            </Stack>

            {!editing ?
                <Box>
                    <InfoRow label={t("profileSettings.personalInfo.firstName", "First name")} value={localFirstName} />
                    <InfoRow label={t("profileSettings.personalInfo.lastName", "Last name")} value={localLastName} />
                    <InfoRow label={t("profileSettings.personalInfo.email", "Email address")} value={email} noBorder />
                </Box>
            :   <Box>
                    <InfoRow label={t("profileSettings.personalInfo.firstName", "First name")}>
                        <TextField
                            value={localFirstName}
                            onChange={(e) => setLocalFirstName(e.target.value)}
                            size="small"
                            fullWidth
                            inputProps={{
                                "aria-label": t("profileSettings.personalInfo.firstName", "First name"),
                            }}
                        />
                    </InfoRow>
                    <InfoRow label={t("profileSettings.personalInfo.lastName", "Last name")}>
                        <TextField
                            value={localLastName}
                            onChange={(e) => setLocalLastName(e.target.value)}
                            size="small"
                            fullWidth
                            inputProps={{
                                "aria-label": t("profileSettings.personalInfo.lastName", "Last name"),
                            }}
                        />
                    </InfoRow>
                    <InfoRow label={t("profileSettings.personalInfo.email", "Email address")} value={email} noBorder />
                    <Stack
                        direction="row"
                        gap={theme.spacing(1.5)}
                        justifyContent="flex-end"
                        sx={{
                            borderTop: `1px solid ${theme.custom.grey[500]}`,
                            px: theme.spacing(4),
                            py: theme.spacing(2),
                        }}
                    >
                        <Button variant="secondary" size="small" onClick={handleCancel}>
                            {t("common.cancel", "Cancel")}
                        </Button>
                        <Button
                            variant="primary"
                            size="small"
                            onClick={handleSave}
                            disabled={updateProfileMutation.isLoading}
                        >
                            {updateProfileMutation.isLoading ?
                                t("common.saving", "Saving...")
                            :   t("common.save", "Save")}
                        </Button>
                    </Stack>
                </Box>
            }
        </Box>
    );
};

// ─── Password Card ────────────────────────────────────────────────────────

const PasswordCard = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const updatePasswordMutation = useUpdateCurrentUserPassword();
    const [editing, setEditing] = useState(false);
    const [currentPwd, setCurrentPwd] = useState("");
    const [newPwd, setNewPwd] = useState("");
    const [confirmPwd, setConfirmPwd] = useState("");
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleCancel = () => {
        setCurrentPwd("");
        setNewPwd("");
        setConfirmPwd("");
        setError(null);
        setEditing(false);
    };

    const handleSave = async () => {
        if (!currentPwd) {
            setError(t("profileSettings.password.error.current_required", "The current password is required."));
            return;
        }
        const { score } = computePasswordStrength(newPwd);
        if (score < 2) {
            setError(t("profileSettings.password.error.too_weak", "The new password is too weak."));
            return;
        }
        if (newPwd !== confirmPwd) {
            setError(t("profileSettings.password.error.mismatch", "The passwords do not match."));
            return;
        }
        try {
            await updatePasswordMutation.mutateAsync({
                plainPassword: newPwd,
            });
            setSnackbarProps({
                message: t("profileSettings.password.update_success", "Password updated."),
                type: "success",
            });
            setError(null);
            handleCancel();
        } catch {
            setError(t("profileSettings.password.error.update_failed", "Failed to update password."));
            setSnackbarProps({
                message: t("profileSettings.password.update_error", "Failed to update password."),
                type: "error",
            });
        }
    };

    const eyeToggle = (visible: boolean, toggle: () => void) => (
        <InputAdornment position="end">
            <IconButton size="small" onClick={toggle} edge="end">
                {visible ?
                    <VisibilityOff fontSize="small" />
                :   <Visibility fontSize="small" />}
            </IconButton>
        </InputAdornment>
    );

    return (
        <Box sx={{ ...sectionStyles, borderRadius: theme.spacing(2), overflow: "hidden" }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    borderBottom: `1px solid ${theme.custom.grey[500]}`,
                    px: theme.spacing(4),
                    py: theme.spacing(2.5),
                }}
            >
                <Typography variant="h3" sx={{ fontSize: "20px", fontWeight: 700 }}>
                    {t("profileSettings.password.title", "Password")}
                </Typography>
                {!editing && (
                    <Button variant="primary" size="small" onClick={() => setEditing(true)}>
                        {t("profileSettings.password.edit", "Edit")}
                    </Button>
                )}
            </Stack>

            {!editing ?
                <InfoRow
                    label={t("profileSettings.password.label", "Password")}
                    value={"\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}
                    noBorder
                />
            :   <Stack gap={theme.spacing(2)} sx={{ p: theme.spacing(4) }}>
                    {error && (
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    )}
                    <TextField
                        label={t("profileSettings.password.current", "Current password")}
                        type={showCurrent ? "text" : "password"}
                        value={currentPwd}
                        onChange={(e) => {
                            setCurrentPwd(e.target.value);
                            setError(null);
                        }}
                        size="small"
                        fullWidth
                        InputProps={{ endAdornment: eyeToggle(showCurrent, () => setShowCurrent((v) => !v)) }}
                    />
                    <Box>
                        <TextField
                            label={t("profileSettings.password.new", "New password")}
                            type={showNew ? "text" : "password"}
                            value={newPwd}
                            onChange={(e) => {
                                setNewPwd(e.target.value);
                                setError(null);
                            }}
                            size="small"
                            fullWidth
                            InputProps={{ endAdornment: eyeToggle(showNew, () => setShowNew((v) => !v)) }}
                        />
                        <StrengthBar password={newPwd} />
                    </Box>
                    <TextField
                        label={t("profileSettings.password.confirm", "Confirm new password")}
                        type={showConfirm ? "text" : "password"}
                        value={confirmPwd}
                        onChange={(e) => {
                            setConfirmPwd(e.target.value);
                            setError(null);
                        }}
                        size="small"
                        fullWidth
                        InputProps={{ endAdornment: eyeToggle(showConfirm, () => setShowConfirm((v) => !v)) }}
                    />
                    <Stack direction="row" gap={theme.spacing(1.5)} justifyContent="flex-end">
                        <Button variant="secondary" size="small" onClick={handleCancel}>
                            {t("common.cancel", "Cancel")}
                        </Button>
                        <Button
                            variant="primary"
                            size="small"
                            onClick={handleSave}
                            disabled={updatePasswordMutation.isLoading}
                        >
                            {updatePasswordMutation.isLoading ?
                                t("common.saving", "Saving...")
                            :   t("profileSettings.password.save", "Update")}
                        </Button>
                    </Stack>
                </Stack>
            }
        </Box>
    );
};

// ─── Main Export ───────────────────────────────────────────────────────────

export const PersonalInfoTab = () => {
    const theme = useTheme();
    const { user } = useFoodpilotContext();

    return (
        <Stack gap={theme.spacing(3)}>
            <PersonalInfoCard
                firstName={user.trustedUser.firstname}
                lastName={user.trustedUser.lastname}
                email={user.trustedUser.email}
            />
            <PasswordCard />
        </Stack>
    );
};
